/**
 * Field rules from Vendor data modification fields.xlsx (one-time import).
 * Runtime source of truth — no Excel file in the app.
 * To regenerate: node scripts/generate-change-request-field-metadata.js "<path-to-xlsx>"
 */

export type ChangeRequestFieldType = "text" | "dropdown" | "list" | "date";

export type ChangeRequestValidationRule =
  | "none"
  | "numeric"
  | "email"
  | "pan"
  | "gst"
  | "ifsc"
  | "indiaPin"
  | "mobileIndia"
  | "freeText"
  | "displayOnly"
  | "date";

export interface ChangeRequestFieldMeta {
  formKey: string;
  portalLabel: string;
  editable: boolean;
  fieldType: ChangeRequestFieldType;
  maxLength?: number;
  validationRule?: ChangeRequestValidationRule;
  validationHint?: string;
  /** When true, empty values fail validation (bank / vendor form controls). */
  required?: boolean;
  supportingDocuments: string[];
}

export const CHANGE_REQUEST_FIELD_METADATA: Record<string, ChangeRequestFieldMeta> = {
  "ACCOUNT_HOLDER": {
    formKey: "ACCOUNT_HOLDER",
    portalLabel: "Account Holder",
    editable: true,
    fieldType: "text",
    maxLength: 60,
    validationRule: "none",
    required: true,
    supportingDocuments: [],
  },
  "ACCOUNT_NO": {
    formKey: "ACCOUNT_NO",
    portalLabel: "Account Number",
    editable: true,
    fieldType: "text",
    maxLength: 18,
    validationRule: "none",
    required: true,
    supportingDocuments: ["Cancelled cheque copy", "Bank mandate form along with Bank stamp\nFor internationl vendor - Authenticated document will be submitted."],
  },
  "BANK_COUNTRY": {
    formKey: "BANK_COUNTRY",
    portalLabel: "Bank Country",
    editable: false,
    fieldType: "text",
    maxLength: 3,
    validationRule: "displayOnly",
    required: true,
    supportingDocuments: [],
  },
  "BANK_NAME": {
    formKey: "BANK_NAME",
    portalLabel: "Bank Name",
    editable: false,
    fieldType: "text",
    maxLength: 60,
    validationRule: "displayOnly",
    required: true,
    supportingDocuments: [],
  },
  "BANK_ADDRESS": {
    formKey: "BANK_ADDRESS",
    portalLabel: "Bank Address",
    editable: false,
    fieldType: "text",
    maxLength: 200,
    validationRule: "displayOnly",
    required: true,
    supportingDocuments: [],
  },
  "BANK_BRANCH": {
    formKey: "BANK_BRANCH",
    portalLabel: "Bank Branch",
    editable: false,
    fieldType: "text",
    maxLength: 100,
    validationRule: "displayOnly",
    required: true,
    supportingDocuments: [],
  },
  "BANK_CITY": {
    formKey: "BANK_CITY",
    portalLabel: "Bank City",
    editable: false,
    fieldType: "text",
    maxLength: 35,
    validationRule: "displayOnly",
    required: true,
    supportingDocuments: [],
  },
  "BANK_STATE_DESC": {
    formKey: "BANK_STATE_DESC",
    portalLabel: "Bank State Desc",
    editable: false,
    fieldType: "text",
    maxLength: 35,
    validationRule: "displayOnly",
    required: true,
    supportingDocuments: [],
  },
  "BANK_STATE_CODE": {
    formKey: "BANK_STATE_CODE",
    portalLabel: "Bank State Code",
    editable: false,
    fieldType: "text",
    maxLength: 2,
    validationRule: "displayOnly",
    required: true,
    supportingDocuments: [],
  },
  "BANK_SWIFT_CODE": {
    formKey: "BANK_SWIFT_CODE",
    portalLabel: "Bank Swift Code",
    editable: false,
    fieldType: "text",
    maxLength: 11,
    validationRule: "displayOnly",
    supportingDocuments: [],
  },
  "CITY": {
    formKey: "CITY",
    portalLabel: "City",
    editable: true,
    fieldType: "dropdown",
    maxLength: 35,
    validationRule: "none",
    validationHint: "Districts Data will derived and displayed based on selected region",
    supportingDocuments: [],
  },
  "COUNTRY": {
    formKey: "COUNTRY",
    portalLabel: "Country",
    editable: false,
    fieldType: "dropdown",
    maxLength: 3,
    validationRule: "displayOnly",
    supportingDocuments: [],
  },
  "EMAIL1": {
    formKey: "EMAIL1",
    portalLabel: "Contact email 1",
    editable: true,
    fieldType: "text",
    maxLength: 241,
    validationRule: "email",
    validationHint: "Genric Email Validation Email accepts either the literal string NA (in any capitalization mix: NA, na, Na, nA) or an email address in one of three formats:  an email with a 2-letter top-level domain (e.g. user@domain.co),  an email with a 3-letter top-level domain (e.g. user@domain.com), or  an email with a 3-letter TLD followed by a dot and 2-letter country part (e.g. user@domain.com.in).  Local part rules: starts and ends with an alphanumeric character; may include letters, digits, underscores (_), dots (.) and hyphens (-) inside. Domain rules: domain labels start and end with an alphanumeric character and may contain dots or hyphens in between.",
    supportingDocuments: ["Email ID change request on vendor letterhead", "Email conversation copy"],
  },
  "EMAIL2": {
    formKey: "EMAIL2",
    portalLabel: "Contact email 2",
    editable: true,
    fieldType: "text",
    maxLength: 241,
    validationRule: "email",
    validationHint: "Genric Email Validation Email accepts either the literal string NA (in any capitalization mix: NA, na, Na, nA) or an email address in one of three formats:  an email with a 2-letter top-level domain (e.g. user@domain.co),  an email with a 3-letter top-level domain (e.g. user@domain.com), or  an email with a 3-letter TLD followed by a dot and 2-letter country part (e.g. user@domain.com.in).  Local part rules: starts and ends with an alphanumeric character; may include letters, digits, underscores (_), dots (.) and hyphens (-) inside. Domain rules: domain labels start and end with an alphanumeric character and may contain dots or hyphens in between.",
    supportingDocuments: ["Email ID change request on vendor letterhead", "Email conversation copy"],
  },
  "EMAIL3": {
    formKey: "EMAIL3",
    portalLabel: "Contact email 3",
    editable: true,
    fieldType: "text",
    maxLength: 241,
    validationRule: "email",
    validationHint: "Genric Email Validation Email accepts either the literal string NA (in any capitalization mix: NA, na, Na, nA) or an email address in one of three formats:  an email with a 2-letter top-level domain (e.g. user@domain.co),  an email with a 3-letter top-level domain (e.g. user@domain.com), or  an email with a 3-letter TLD followed by a dot and 2-letter country part (e.g. user@domain.com.in).  Local part rules: starts and ends with an alphanumeric character; may include letters, digits, underscores (_), dots (.) and hyphens (-) inside. Domain rules: domain labels start and end with an alphanumeric character and may contain dots or hyphens in between.",
    supportingDocuments: ["Email ID change request on vendor letterhead", "Email conversation copy"],
  },
  "EMAIL4": {
    formKey: "EMAIL4",
    portalLabel: "Contact email 4",
    editable: true,
    fieldType: "text",
    maxLength: 241,
    validationRule: "email",
    validationHint: "Genric Email Validation Email accepts either the literal string NA (in any capitalization mix: NA, na, Na, nA) or an email address in one of three formats:  an email with a 2-letter top-level domain (e.g. user@domain.co),  an email with a 3-letter top-level domain (e.g. user@domain.com), or  an email with a 3-letter TLD followed by a dot and 2-letter country part (e.g. user@domain.com.in).  Local part rules: starts and ends with an alphanumeric character; may include letters, digits, underscores (_), dots (.) and hyphens (-) inside. Domain rules: domain labels start and end with an alphanumeric character and may contain dots or hyphens in between.",
    supportingDocuments: ["Email ID change request on vendor letterhead", "Email conversation copy"],
  },
  "EMAIL5": {
    formKey: "EMAIL5",
    portalLabel: "Contact email 5",
    editable: true,
    fieldType: "text",
    maxLength: 241,
    validationRule: "email",
    validationHint: "Genric Email Validation Email accepts either the literal string NA (in any capitalization mix: NA, na, Na, nA) or an email address in one of three formats:  an email with a 2-letter top-level domain (e.g. user@domain.co),  an email with a 3-letter top-level domain (e.g. user@domain.com), or  an email with a 3-letter TLD followed by a dot and 2-letter country part (e.g. user@domain.com.in).  Local part rules: starts and ends with an alphanumeric character; may include letters, digits, underscores (_), dots (.) and hyphens (-) inside. Domain rules: domain labels start and end with an alphanumeric character and may contain dots or hyphens in between.",
    supportingDocuments: ["Email ID change request on vendor letterhead", "Email conversation copy"],
  },
  "GST_CLASSIFICATION": {
    formKey: "GST_CLASSIFICATION",
    portalLabel: "Tax Registration Status (GST Number)",
    editable: false,
    fieldType: "list",
    maxLength: 1,
    validationRule: "displayOnly",
    validationHint: "Registered(N), Non Registered(0), Special Economic Zone(2), Compounding Scheme(3)",
    supportingDocuments: [],
  },
  "GST_NUMBER": {
    formKey: "GST_NUMBER",
    portalLabel: "Tax Registration Number(GST Number)",
    editable: true,
    fieldType: "text",
    maxLength: 15,
    validationRule: "gst",
    validationHint: "Format: State Code (2 digits) + PAN (10 chars) + Entity Code (1 digit/letter) + Z + Checksum (1 digit/letter) State Code: 01\u201337 (India state codes) and it should match State in Vendor Address PAN inside GST must follow PAN rules and matches the PAN already available or provided one If PAN changed then GST No must change. If State Code is changed GST No should be changed if State Code mismatches   Also checking with 3rd Party API GST Number Validation",
    supportingDocuments: ["GST Copy"],
  },
  "IFSC_CODE": {
    formKey: "IFSC_CODE",
    portalLabel: "IFSC code",
    editable: true,
    fieldType: "dropdown",
    maxLength: 15,
    validationRule: "ifsc",
    required: true,
    validationHint: "For India IFSC Code 11 Digit (Bank Code(4)+ 1 digit + Branch Code(6)), other countries considering Swift code as Bank Key",
    supportingDocuments: ["Cancelled cheque copy", "Bank mandate form along with Bank stamp\nFor internationl vendor - Authenticated document will be submitted."],
  },
  "MOBILE_NO1": {
    formKey: "MOBILE_NO1",
    portalLabel: "Contact mobile 1",
    editable: true,
    fieldType: "text",
    maxLength: 20,
    validationRule: "mobileIndia",
    validationHint: "Numeric, \"+\" & Tel Country Code&\"-\"&Mobile No, If Country is India, mobile must 10 digit only, for other countries Max 15 Numbers",
    supportingDocuments: ["Contact details change request on vendor letterhead", "Email conversation"],
  },
  "MOBILE_NO2": {
    formKey: "MOBILE_NO2",
    portalLabel: "Contact mobile 2",
    editable: true,
    fieldType: "text",
    maxLength: 20,
    validationRule: "mobileIndia",
    validationHint: "Numeric, \"+\" & Tel Country Code&\"-\"&Mobile No, If Country is India, mobile must 10 digit only, for other countries Max 15 Numbers",
    supportingDocuments: ["Contact details change request on vendor letterhead", "Email conversation"],
  },
  "MOBILE_NO3": {
    formKey: "MOBILE_NO3",
    portalLabel: "Contact mobile 3",
    editable: true,
    fieldType: "text",
    maxLength: 20,
    validationRule: "mobileIndia",
    validationHint: "Numeric, \"+\" & Tel Country Code&\"-\"&Mobile No, If Country is India, mobile must 10 digit only, for other countries Max 15 Numbers",
    supportingDocuments: ["Contact details change request on vendor letterhead", "Email conversation"],
  },
  "MSME_NO": {
    formKey: "MSME_NO",
    portalLabel: "URN number(MSME No)",
    editable: true,
    fieldType: "text",
    maxLength: 19,
    validationRule: "none",
    validationHint: "EX Format: UDYAM-AP-01-234567",
    supportingDocuments: ["Latest MSME Certificate"],
  },
  "MSME_STATUS": {
    formKey: "MSME_STATUS",
    portalLabel: "Industry (micro, small, medium) (MSME Status)",
    editable: true,
    fieldType: "text",
    validationRule: "none",
    supportingDocuments: [],
  },
  "MSME_VALID_END_DATE": {
    formKey: "MSME_VALID_END_DATE",
    portalLabel: "MSME validity end date",
    editable: true,
    fieldType: "date",
    validationRule: "date",
    supportingDocuments: [],
  },
  "MSME_VALID_START_DATE": {
    formKey: "MSME_VALID_START_DATE",
    portalLabel: "MSME validity start date",
    editable: true,
    fieldType: "date",
    validationRule: "date",
    supportingDocuments: [],
  },
  "PAN_NUMBER": {
    formKey: "PAN_NUMBER",
    portalLabel: "PAN number",
    editable: true,
    fieldType: "text",
    maxLength: 10,
    validationRule: "pan",
    validationHint: "Total Length: 10 characters Pattern: 5 Alphabets  + 4 Numbers + 1  Alphabet Restriction: 4th letter must be one of P, C, H, F, A, T, B, L, J, G.",
    supportingDocuments: ["PAN Copy"],
  },
  "POSTAL_CODE": {
    formKey: "POSTAL_CODE",
    portalLabel: "Postal Code",
    editable: true,
    fieldType: "text",
    maxLength: 10,
    validationRule: "indiaPin",
    validationHint: "For Inida 6 Digit numeric",
    supportingDocuments: [],
  },
  "SAP_VENDOR_CODE": {
    formKey: "SAP_VENDOR_CODE",
    portalLabel: "Vendor code",
    editable: false,
    fieldType: "text",
    maxLength: 10,
    validationRule: "displayOnly",
    validationHint: "Display Only",
    supportingDocuments: [],
  },
  "REQUESTOR_ID": {
    formKey: "REQUESTOR_ID",
    portalLabel: "Requestor Id",
    editable: false,
    fieldType: "text",
    validationRule: "displayOnly",
    supportingDocuments: [],
  },
  "STATE_CODE": {
    formKey: "STATE_CODE",
    portalLabel: "Region",
    editable: true,
    fieldType: "dropdown",
    maxLength: 3,
    validationRule: "none",
    supportingDocuments: [],
  },
  "STREET1": {
    formKey: "STREET1",
    portalLabel: "Address (line 1)",
    editable: true,
    fieldType: "text",
    maxLength: 40,
    validationRule: "freeText",
    validationHint: "Free Text",
    supportingDocuments: ["GST Copy"],
  },
  "STREET2": {
    formKey: "STREET2",
    portalLabel: "Address (line 2)",
    editable: true,
    fieldType: "text",
    maxLength: 60,
    validationRule: "freeText",
    validationHint: "Free Text",
    supportingDocuments: ["GST Copy"],
  },
  "STREET3": {
    formKey: "STREET3",
    portalLabel: "Address (line 3)",
    editable: true,
    fieldType: "text",
    maxLength: 40,
    validationRule: "freeText",
    validationHint: "Free Text",
    supportingDocuments: ["GST Copy"],
  },
  "STREET4": {
    formKey: "STREET4",
    portalLabel: "Address (line 4)",
    editable: true,
    fieldType: "text",
    maxLength: 40,
    validationRule: "freeText",
    validationHint: "Free Text",
    supportingDocuments: ["GST Copy"],
  },
  "VENDOR_NAME1": {
    formKey: "VENDOR_NAME1",
    portalLabel: "Vendor name (line 1)",
    editable: true,
    fieldType: "text",
    maxLength: 35,
    validationRule: "none",
    supportingDocuments: ["Certificate of Incorporation", "GST & PAN"],
  },
  "VENDOR_NAME2": {
    formKey: "VENDOR_NAME2",
    portalLabel: "Vendor name (line 2)",
    editable: true,
    fieldType: "text",
    maxLength: 35,
    validationRule: "none",
    supportingDocuments: ["Certificate of Incorporation", "GST & PAN"],
  },
  "VENDOR_NAME3": {
    formKey: "VENDOR_NAME3",
    portalLabel: "Vendor name (line 3)",
    editable: true,
    fieldType: "text",
    maxLength: 35,
    validationRule: "none",
    supportingDocuments: ["Certificate of Incorporation", "GST & PAN"],
  },
  "VENDOR_NAME4": {
    formKey: "VENDOR_NAME4",
    portalLabel: "Vendor name (line 4)",
    editable: true,
    fieldType: "text",
    maxLength: 35,
    validationRule: "none",
    supportingDocuments: ["Certificate of Incorporation", "GST & PAN"],
  },
};

export const SAP_KEY_TO_FORM_KEY: Record<string, string> = {
  "BANKA": "BANK_NAME",
  "BANKL": "IFSC_CODE",
  "BANKN": "ACCOUNT_NO",
  "BANKS": "BANK_COUNTRY",
  "J_1IPANNO": "PAN_NUMBER",
  "KOINH": "ACCOUNT_HOLDER",
  "LAND1": "COUNTRY",
  "LIFNR": "SAP_VENDOR_CODE",
  "MSME_END_DATE": "MSME_VALID_END_DATE",
  "MSME_NO": "MSME_NO",
  "MSME_START_DATE": "MSME_VALID_START_DATE",
  "MSME_STATUS": "MSME_STATUS",
  "NAME1": "VENDOR_NAME1",
  "NAME2": "VENDOR_NAME2",
  "NAME3": "VENDOR_NAME3",
  "NAME4": "VENDOR_NAME4",
  "ORT01": "CITY",
  "PSTLZ": "POSTAL_CODE",
  "REGIO": "STATE_CODE",
  "SMTP_ADDR": "EMAIL1",
  "SMTP_ADDR2": "EMAIL2",
  "SMTP_ADDR3": "EMAIL3",
  "SMTP_ADDR4": "EMAIL4",
  "SMTP_ADDR5": "EMAIL5",
  "STCD3": "GST_NUMBER",
  "STREET": "STREET2",
  "STR_SUPPL1": "STREET1",
  "STR_SUPPL2": "STREET3",
  "STR_SUPPL3": "STREET4",
  "TELF2": "MOBILE_NO1",
  "TELF_MOBILE2": "MOBILE_NO2",
  "TELF_MOBILE3": "MOBILE_NO3",
  "VEN_CLASS": "GST_CLASSIFICATION",
};
